/** @jsxImportSource @emotion/react */
import { DeleteOutlined } from '@ant-design/icons'
import css from '@emotion/css/macro'
import req from '@sportninja/common/api/request'
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import React from 'react'
import { paragraphMedium, weightSemiBold } from 'src/components/css'
import DeleteModal from 'src/components/Modal/DeleteModal'
import { SwitchInput } from 'src/components/ts-components/SwitchInput'
import Text from 'src/components/ts-components/Text'
import { TextInput } from 'src/components/ts-components/TextInput'
import { Separator } from '../GeneralForm'
import { useScheduleContext } from '../ScheduleContext'
import '../styles.scss'

const ReprocessHiddenButton = ({ ...props }) => {
  return (
    <button
      id='reprocess-hidden-button'
      css={css`
        display: flex;
        padding: 8px 16px 8px 8px;
        justify-content: center;
        align-items: center;
        gap: 8px;
        border-radius: 8px;
        border: 1px solid ${colors.ERROR_200};
        box-shadow: 0px 2px 0px 0px rgba(5, 145, 255, 0.1);
        /* hide the button */
        display: none;
      `}
      {...props}
    >
      <DeleteOutlined
        css={css`
          color: ${colors.ERROR_200};
        `}
        width={24}
        height={24}
      />
      <p
        css={css`
          ${paragraphMedium}
          ${weightSemiBold}
          font-family: ${isCanlan
            ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
            : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
          color: ${colors.WHITE};
        `}
      >
        DELETE {isCanlan ? 'SEASON' : 'COMPETITION'}
      </p>
    </button>
  )
}

type SettingsFormProps = {
  onCancelPressed: () => void
  triggerRefresh: () => void
  addPending: (id: string | undefined) => void
  isMobile: boolean
}

const SettingsForm: React.FC<SettingsFormProps> = ({
  onCancelPressed,
  triggerRefresh,
  addPending,
  isMobile,
}) => {
  const {
    id,
    fieldErrors,
    customError,
    skipTeamOfficialsSignatures,
    setSkipTeamOfficialsSignatures,
    skipOfficialsSignatures,
    setSkipOfficialsSignatures,
    timingPeriodLength,
    setTimingPeriodLength,
    timingExtraPeriodLength,
    setTimingExtraPeriodLength,
    requireGameOfficials,
    setRequireGameOfficials,
    goalsPerForfeit,
    wins,
    ties,
    losses,
    overtimerWin,
    overtimeLoss,
    shootoutLoss,
    setGoalsPerForfeit,
    setWins,
    setTies,
    setLosses,
    setOvertimeWin,
    setOvertimeLoss,
    setShootoutLoss,
    onSubmit,
    onCancel,
    requireUniqueJerseyNumbers,
    setRequireUniqueJerseyNumbers,
    requireWaiverSignature,
    setRequireWaiverSignature,
  } = useScheduleContext()

  return (
    <div
      css={css`
        padding-top: 16px;
        padding-left: 16px;
        padding-right: 16px;
        padding-bottom: 16px;
        display: flex;
        flex: 1;
        flex-direction: column;
        overflow-y: auto;
        height: 100%;
        gap: 16px;
        border-bottom: 1px solid #3f4753;
        ${isMobile &&
        css`
          padding-left: 8px;
          padding-right: 8px;
          width: 100%;
        `}
      `}
    >
      <DeleteModal
        title={'CONFIRM AND REPROCESS COMPETITION STATISTICS'}
        message={
          'Reprocess statistics when you have made edits to the Points System, and want to update the statistics/standings for the Competition.'
        }
        secondaryMessage={
          'NOTE: Depending on the number of games, this can take several minutes to complete.'
        }
        onDelete={async (_shouldForce = false) => {
          await onSubmit()
          await req(`/schedules/${id}/reprocess`)
          addPending(id)
          triggerRefresh()
          onCancel()
          onCancelPressed()
        }}
        Button={ReprocessHiddenButton}
        useForceDelete={true}
        forceDeleteText={undefined}
        buttonText='Reprocess'
        bannerText='Reprocessing Stats'
        displayBanner={false}
      />
      <div
        css={css`
          display: flex;
          flex-direction: column;
          width: 100%;
          gap: 16px;
        `}
      >
        {/* Game Setup Settings */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-end;
            gap: 16px;
            align-self: stretch;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: flex-start;
              gap: 8px;
              align-self: stretch;
            `}
          >
            <Text
              variant='captionLarge'
              weight='semiBold'
              color={colors.NEUTRAL_0}
            >
              Game Setup Settings
            </Text>
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              align-self: stretch;
            `}
          >
            <SwitchInput
              title='Skip Team Official Setup'
              subtitle='This option allows you to skip the setup of team officials.'
              value={skipTeamOfficialsSignatures}
              onChange={setSkipTeamOfficialsSignatures}
            />
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              align-self: stretch;
            `}
          >
            <SwitchInput
              title='Require Game Officials'
              subtitle='This option makes at least one game official required for all games.'
              value={requireGameOfficials}
              onChange={setRequireGameOfficials}
            />
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              align-self: stretch;
            `}
          >
            <SwitchInput
              title='Skip Game Officials Signatures'
              subtitle='This option allows you to skip the signatures of officials.'
              value={skipOfficialsSignatures}
              onChange={setSkipOfficialsSignatures}
            />
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              align-self: stretch;
            `}
          >
            <SwitchInput
              title='Require Unique Jersey Numbers'
              subtitle='This option will enforce all players have a unique Jersey # when setting up a game.'
              value={requireUniqueJerseyNumbers}
              onChange={setRequireUniqueJerseyNumbers}
            />
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              align-self: stretch;
            `}
          >
            <SwitchInput
              title='Require Waiver Signature Before Player Can Play'
              subtitle='This option will requires players to sign the waiver before they can be added to a game'
              value={requireWaiverSignature}
              onChange={setRequireWaiverSignature}
            />
          </div>
          {/* For now, this is being disabled */}
          {/* <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              align-self: stretch;
            `}
          >
            <SwitchInput
              title='Skip Starting Line-up Selection'
              subtitle='This option allows you to skip the starting line-up.'
              value={skipStartingLineupSelection}
              onChange={setSkipStartingLineupSelection}
            />
          </div> */}
        </div>
        <Separator />
        {/* Timing Settings */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-end;
            gap: 16px;
            align-self: stretch;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: flex-start;
              gap: 8px;
              align-self: stretch;
            `}
          >
            <Text
              variant='captionLarge'
              weight='semiBold'
              color={colors.NEUTRAL_0}
            >
              Timing Settings
            </Text>
          </div>
          <div
            css={css`
              display: flex;
              align-items: flex-start;
              gap: 16px;
              align-self: stretch;
              ${isMobile &&
              css`
                width: 100%;
                flex-direction: column;
              `}
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                flex: 1 0 0;
                width: 100%;
              `}
            >
              <TextInput
                name={'timingPeriodLength'}
                value={
                  timingPeriodLength !== null
                    ? timingPeriodLength.toString()
                    : ''
                }
                setValue={(value) =>
                  setTimingPeriodLength(value ? parseFloat(value) : null)
                }
                fieldErrors={fieldErrors}
                customError={customError}
                label='Default Period Length'
                type='number'
              />
            </div>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                flex: 1 0 0;
                width: 100%;
              `}
            >
              <TextInput
                name={'timingExtraPeriodLength'}
                value={
                  timingExtraPeriodLength !== null
                    ? timingExtraPeriodLength.toString()
                    : ''
                }
                setValue={(value) =>
                  setTimingExtraPeriodLength(value ? parseFloat(value) : null)
                }
                fieldErrors={fieldErrors}
                customError={customError}
                label='Default Overtime Length'
                type='number'
              />
            </div>
          </div>
        </div>
        <Separator />
        {/* Point System */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-end;
            gap: 16px;
            align-self: stretch;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: flex-start;
              gap: 8px;
              align-self: stretch;
            `}
          >
            <Text
              variant='captionLarge'
              weight='semiBold'
              color={colors.NEUTRAL_0}
            >
              Point System
            </Text>
          </div>
          <div
            css={css`
              display: flex;
              align-items: flex-start;
              gap: 16px;
              align-self: stretch;
              ${isMobile &&
              css`
                width: 100%;
                flex-direction: column;
              `}
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                flex: 1 0 0;
                width: 100%;
              `}
            >
              <TextInput
                name={'wins'}
                value={wins !== null ? wins.toString() : ''}
                setValue={(value) => setWins(value ? parseFloat(value) : null)}
                fieldErrors={fieldErrors}
                customError={customError}
                label='Wins'
                type='number'
              />
            </div>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                flex: 1 0 0;
                width: 100%;
              `}
            >
              <TextInput
                name={'ties'}
                value={ties !== null ? ties.toString() : ''}
                setValue={(value) => setTies(value ? parseFloat(value) : null)}
                fieldErrors={fieldErrors}
                customError={customError}
                label='Ties'
                type='number'
              />
            </div>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                flex: 1 0 0;
                width: 100%;
              `}
            >
              <TextInput
                name={'losses'}
                value={losses !== null ? losses.toString() : ''}
                setValue={(value) =>
                  setLosses(value ? parseFloat(value) : null)
                }
                fieldErrors={fieldErrors}
                customError={customError}
                label='Losses'
                type='number'
              />
            </div>
          </div>
          <div
            css={css`
              display: flex;
              align-items: flex-start;
              gap: 16px;
              align-self: stretch;
              ${isMobile &&
              css`
                width: 100%;
                flex-direction: column;
              `}
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                flex: 1 0 0;
                width: 100%;
              `}
            >
              <TextInput
                name={'overtimerWin'}
                value={overtimerWin !== null ? overtimerWin.toString() : ''}
                setValue={(value) =>
                  setOvertimeWin(value ? parseFloat(value) : null)
                }
                fieldErrors={fieldErrors}
                customError={customError}
                label='Overtime Win'
                type='number'
              />
            </div>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                flex: 1 0 0;
                width: 100%;
              `}
            >
              <TextInput
                name={'overtimeLoss'}
                value={overtimeLoss !== null ? overtimeLoss.toString() : ''}
                setValue={(value) =>
                  setOvertimeLoss(value ? parseFloat(value) : null)
                }
                fieldErrors={fieldErrors}
                customError={customError}
                label='Overtime Losses'
                type='number'
              />
            </div>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                flex: 1 0 0;
                width: 100%;
              `}
            >
              <TextInput
                name={'shootoutLoss'}
                value={shootoutLoss !== null ? shootoutLoss.toString() : ''}
                setValue={(value) =>
                  setShootoutLoss(value ? parseFloat(value) : null)
                }
                fieldErrors={fieldErrors}
                customError={customError}
                label='Shootout Losses'
                type='number'
              />
            </div>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                flex: 1 0 0;
                width: 100%;
              `}
            >
              <TextInput
                name={'goalsPerForfeit'}
                value={
                  goalsPerForfeit !== null ? goalsPerForfeit.toString() : ''
                }
                setValue={(value) =>
                  setGoalsPerForfeit(value ? parseFloat(value) : null)
                }
                fieldErrors={fieldErrors}
                customError={customError}
                label='Goals Per Forfeit'
                type='number'
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SettingsForm
